import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useMatchupBoxscore } from '../../hooks/useMatchupBoxscore';
import { BoxscorePlayer, BoxscorePlayerStats, BoxscoreTeam } from '../../types';
import { enabledColumns, formatStat, ScoreColumn } from './scoreboardColumns';
import BackLink from '../../components/common/BackLink';

// ─────────────────────────────────────────────
// One player: stat-line row, click to expand full breakdown
// ─────────────────────────────────────────────

const valueFor = (col: ScoreColumn, stats: BoxscorePlayerStats): number =>
  col.derive ? col.derive(stats) : ((stats[col.statField!] ?? 0) as number);

function PlayerRow({ player, cols }: { player: BoxscorePlayer; cols: ScoreColumn[] }) {
  const [open, setOpen] = useState(false);
  const stats = (player.stats ?? {}) as BoxscorePlayerStats;
  const isStarter = player.is_starter;
  const nonZero = cols.filter(c => Number(valueFor(c, stats)) !== 0);

  return (
    <div className={`border-b border-secondary-100 last:border-0 ${isStarter ? '' : 'opacity-60'}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-secondary-50 transition-colors"
      >
        <span className="w-3 shrink-0 text-xs text-secondary-400" aria-hidden="true">
          {isStarter ? '★' : '—'}
        </span>

        <div className="min-w-0 flex-1">
          <div className="font-medium text-ink-900 truncate">{player.name}</div>
          {nonZero.length > 0 && (
            <div className="mt-0.5 flex flex-wrap gap-x-2.5 gap-y-0.5 text-xs text-secondary-500">
              {nonZero.map(c => (
                <span key={c.key} className="inline-flex items-baseline gap-1 whitespace-nowrap">
                  <span className="font-semibold uppercase tracking-wide">{c.label}</span>
                  <span className="tabular-nums text-secondary-700">{formatStat(valueFor(c, stats), c.format)}</span>
                </span>
              ))}
            </div>
          )}
        </div>

        <span className="font-display text-xl leading-none text-ink-900 tabular-nums shrink-0">
          {(player.fantasy_points ?? 0).toFixed(1)}
        </span>
        <ChevronDownIcon
          aria-hidden="true"
          className={`w-4 h-4 shrink-0 text-secondary-400 transition-transform motion-reduce:transition-none ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {open && (
        <dl className="grid grid-cols-3 sm:grid-cols-4 gap-x-4 gap-y-3 px-4 pb-4 pl-10 bg-secondary-50/60">
          {cols.map(c => (
            <div key={c.key} className="flex flex-col">
              <dt className="text-[0.65rem] font-semibold uppercase tracking-wider text-secondary-400">
                {c.label}
              </dt>
              <dd className="text-sm tabular-nums text-ink-900">
                {formatStat(valueFor(c, stats), c.format)}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// A K or P group within a team
// ─────────────────────────────────────────────

function PositionGroup({
  players,
  cols,
  position,
}: {
  players: BoxscorePlayer[];
  cols: ScoreColumn[];
  position: 'K' | 'P';
}) {
  const filtered = players
    .filter(p => p.position === position)
    .sort((a, b) => {
      if (a.is_starter !== b.is_starter) return a.is_starter ? -1 : 1;
      return b.fantasy_points - a.fantasy_points;
    });

  if (filtered.length === 0) return null;

  return (
    <div>
      <h4 className="text-xs font-semibold uppercase tracking-wider text-secondary-500 px-4 py-2 bg-secondary-50 border-b border-secondary-200">
        {position === 'K' ? 'Kickers' : 'Punters'}
      </h4>
      {filtered.map(p => (
        <PlayerRow key={p.player_id} player={p} cols={cols} />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────
// One team section
// ─────────────────────────────────────────────

function TeamSection({
  team,
  kCols,
  pCols,
  isCompleted,
  outcome,
}: {
  team: BoxscoreTeam;
  kCols: ScoreColumn[];
  pCols: ScoreColumn[];
  isCompleted: boolean;
  outcome: 'win' | 'loss' | 'tie';
}) {
  const badge = { win: 'WIN', loss: 'LOSS', tie: 'TIE' }[outcome];
  const badgeColor = outcome === 'win' ? 'text-success-700' : 'text-secondary-400';

  return (
    <div className="card">
      <div className="card-header flex items-baseline justify-between gap-4">
        <div className="flex items-baseline gap-3 min-w-0">
          <h3 className="font-display uppercase text-lg tracking-wide text-ink-900 truncate">
            {team.team_name}
          </h3>
          {isCompleted && <span className={`text-xs font-bold shrink-0 ${badgeColor}`}>{badge}</span>}
        </div>
        <span
          data-testid="team-total"
          className="font-display text-2xl leading-none text-ink-900 tabular-nums shrink-0"
        >
          {team.total_score.toFixed(1)}
        </span>
      </div>
      <PositionGroup players={team.players} cols={kCols} position="K" />
      <PositionGroup players={team.players} cols={pCols} position="P" />
    </div>
  );
}

// ─────────────────────────────────────────────
// Head-to-head summary bar (keeps the comparison when teams are stacked)
// ─────────────────────────────────────────────

function SummaryBar({
  team1, team2, week, isFinal,
}: {
  team1: BoxscoreTeam; team2: BoxscoreTeam; week?: number; isFinal?: boolean;
}) {
  const s1 = team1.total_score;
  const s2 = team2.total_score;
  const tie = s1 === s2;
  const started = s1 > 0 || s2 > 0;

  // Gold marks the leader and nothing else here — on an ink field it is the
  // one thing the eye should find first. Before anyone has kicked there is no
  // leader to mark, so both sides stay paper.
  const side = (mine: number, theirs: number) =>
    !started || tie ? 'text-paper' : mine > theirs ? 'text-gold' : 'text-paper-400';

  const Team = ({ team, score, other, align }: {
    team: BoxscoreTeam; score: number; other: number; align: 'left' | 'right';
  }) => {
    const leads = started && !tie && score > other;
    return (
      <div className={align === 'right' ? 'text-right min-w-0' : 'text-left min-w-0'}>
        <div className="font-display uppercase text-sm sm:text-lg tracking-wide text-paper-300 truncate">
          {team.team_name}
        </div>
        {/* inline-block so the rule below measures the number, not the column */}
        <div className="inline-block">
          <div className={`mt-1 font-display text-stat-lg tabular-nums ${side(score, other)}`}>
            {score.toFixed(1)}
          </div>
          {/* The leader's rule draws itself in on load — the one piece of
              motion on the screen, and it lands on the thing you opened the
              page to find out. */}
          <div
            className={`mt-2 h-1 bg-gold scoreboard-rule ${
              align === 'right' ? 'origin-right' : 'origin-left'
            } ${leads ? '' : 'invisible'}`}
            aria-hidden="true"
          />
        </div>
      </div>
    );
  };

  return (
    // Breaks the page gutter: a scoreboard is not a card on a page, it is the
    // field the page is played on.
    <div className="-mx-4 sm:-mx-6 lg:-mx-8 bg-ink-900 border-y-2 border-ink-900 on-ink">
      <div className="px-4 sm:px-8 py-6 sm:py-9">
        <h1 className="text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-paper-400 mb-4 sm:mb-6">
          {week ? `Week ${week}` : 'Matchup'}
          {' · '}
          {isFinal ? 'Final' : started ? 'In progress' : 'Not started'}
        </h1>
        <div className="grid grid-cols-[1fr_auto_1fr] items-start gap-3 sm:gap-8">
          <Team team={team1} score={s1} other={s2} align="left" />
          <div className="self-stretch w-px bg-paper-400/25 mt-6" aria-hidden="true" />
          <Team team={team2} score={s2} other={s1} align="right" />
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────

export default function MatchupScoreboardPage() {
  const { leagueId, matchupId } = useParams<{ leagueId: string; matchupId: string }>();
  const { data, loading, error, lastUpdatedAt, refresh } = useMatchupBoxscore(Number(matchupId));

  const backLink = `/app/leagues/${leagueId ?? ''}`;

  if (loading) {
    return (
      <div className="space-y-6">
        <BackLink to={backLink}>Back to League</BackLink>
        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-center py-12">
              <div className="loading-spinner w-8 h-8 mr-3" />
              <span>Loading scoreboard...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="space-y-6">
        <BackLink to={backLink}>Back to League</BackLink>
        <div className="card">
          <div className="card-body text-center py-12">
            <p className="text-danger-600 mb-4">{error ?? 'Could not load the scoreboard.'}</p>
            <Link to={backLink} className="btn-primary">
              Back to League
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const knobs = data.scoring ?? ({} as any);
  const kCols = enabledColumns('K', knobs);
  const pCols = enabledColumns('P', knobs);

  const s1 = data.team1.total_score;
  const s2 = data.team2.total_score;
  const t1Outcome: 'win' | 'loss' | 'tie' = s1 === s2 ? 'tie' : s1 > s2 ? 'win' : 'loss';
  const t2Outcome: 'win' | 'loss' | 'tie' = s1 === s2 ? 'tie' : s2 > s1 ? 'win' : 'loss';
  const secsAgo = lastUpdatedAt != null ? Math.round((Date.now() - lastUpdatedAt) / 1000) : 0;

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <BackLink to={backLink}>Back to League</BackLink>

        {!data.is_completed && (
          <div className="flex items-center gap-3 text-sm">
            <span className="text-secondary-500">Auto-refresh · updated {secsAgo}s ago</span>
            <button onClick={refresh} className="btn-outline-secondary btn-sm">
              Refresh
            </button>
          </div>
        )}
      </div>

      {/* ── Head-to-head bar ── */}
      <SummaryBar team1={data.team1} team2={data.team2} week={data.week} isFinal={data.is_completed} />

      {/* ── Stacked team sections ── */}
      <div className="space-y-6">
        <TeamSection team={data.team1} kCols={kCols} pCols={pCols} isCompleted={data.is_completed} outcome={t1Outcome} />
        <TeamSection team={data.team2} kCols={kCols} pCols={pCols} isCompleted={data.is_completed} outcome={t2Outcome} />
      </div>
    </div>
  );
}
